/** Receiving a shipment, adjusting stock, and counting the shelves. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CatalogItem } from '@pos/shared';
import { useI18n, usePlural, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useToast } from '../../core/state/toast.js';
import { useSync } from '../../core/sync.js';
import { api } from '../../core/api.js';
import { findByBarcode, searchItems, searchStyles, type StyleMatch } from '../../core/db.js';
import { listenForScanner } from '../../core/scanner.js';
import { beepOk, beepUnknown } from '../../core/sound.js';
import { Button, Confirm, Empty, Field, Loading, Modal, MoneyInput, Pill, Swatch } from '../../core/ui/ui.js';
import { CameraButton } from '../../core/ui/bits.js';
import { LabelPrinter } from '../catalog/Labels.js';
import { ProductCreator, useRefs } from '../catalog/Products.js';

/** A search box that adds whatever is scanned or picked. Shared by all three. */
function ItemPicker({ onPick }: { onPick: (i: CatalogItem) => void }) {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);

  const scan = useCallback(
    async (code: string) => {
      const item = await findByBarcode(code);
      if (item) {
        beepOk();
        onPick(item);
      } else {
        try {
          onPick(await api<CatalogItem>(`/catalog/barcode/${encodeURIComponent(code)}`));
          beepOk();
        } catch {
          beepUnknown();
          toast.warn(t('checkout.unknownBarcode', { code }));
        }
      }
    },
    [onPick, t, toast],
  );

  useEffect(() => listenForScanner({ onScan: (c) => void scan(c) }), [scan]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      try {
        setResults(await api<CatalogItem[]>(`/catalog/search?q=${encodeURIComponent(q)}`));
      } catch {
        setResults(await searchItems(q, 30));
      }
    }, 180);
    return () => clearTimeout(timer);
  }, [q]);

  const nm = (i: CatalogItem) => (lang === 'ar' ? i.nameAr || i.nameEn : i.nameEn || i.nameAr);

  return (
    <div>
      <div className="row">
        <input
          type="search"
          data-scan-target="true"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('common.searchPlaceholder')} aria-label={t('common.searchPlaceholder')}
          autoFocus
        />
        <CameraButton onScan={(c) => void scan(c)} />
      </div>
      {results.length > 0 ? (
        <div className="col" style={{ gap: 'var(--s-1)', marginBlockStart: 'var(--s-2)', maxHeight: 260, overflowY: 'auto' }}>
          {results.slice(0, 20).map((r) => (
            <button
              key={r.variantId}
              className="pick-row"
              onClick={() => {
                onPick(r);
                setQ('');
                setResults([]);
              }}
            >
              <span style={{ flex: 1, textAlign: 'start' }}>
                <span className="strong">{nm(r)}</span>
                <span className="xsmall muted">
                  {' '}· {r.size} · <Swatch hex={r.colorHex} /> {lang === 'ar' ? r.colorAr : r.colorEn}
                </span>
              </span>
              <span className="xsmall muted">{r.stock}</span>
              <span className="tnum">{money(r.price)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Receiving picker: find the STYLE, then say how many of each size and colour.
 *
 * The variant picker above is right for counting and adjusting, where you deal
 * with one specific piece. It is wrong for unpacking a box: searching "blazer"
 * returned every size and colour as its own row — thirty of them for one
 * garment — and each had to be found separately. Here one search gives one
 * blazer, and the quantities go into a grid.
 *
 * A scanned barcode still adds that exact piece straight away; someone
 * scanning knows precisely what they are holding.
 */
interface StylePickerProps {
  /** What the delivery already holds, by variant - so reopening a style shows
   *  the counts already entered rather than starting blank and doubling them. */
  current: Record<string, number>;
  /** A scanned piece: one more of that exact variant. */
  onScanned: (item: CatalogItem) => void;
  /** The grid was filled in: this is now the whole of that style's delivery. */
  onSetStyle: (productId: string, picks: { item: CatalogItem; qty: number }[]) => void;
  /** Tells the screen a grid is open, so it can get everything else out of the way. */
  onOpenChange?: (open: boolean) => void;
}

function StylePicker({ current, onScanned, onSetStyle, onOpenChange }: StylePickerProps) {
  const t = useT();
  const plural = usePlural();
  const { lang } = useI18n();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [styles, setStyles] = useState<StyleMatch[]>([]);
  const [open, setOpen] = useState<StyleMatch | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});

  /*
   * The grid stays open while a box is unpacked, so a scan belongs in the box
   * it is standing in front of: if the piece is part of the open style its own
   * square goes up by one, and the grid fills itself as the box empties.
   * Anything else - a different style - goes straight onto the delivery.
   */
  const take = useCallback(
    (item: CatalogItem) => {
      if (open && item.productId === open.productId) {
        setQty((m) => ({ ...m, [item.variantId]: (m[item.variantId] ?? 0) + 1 }));
        return;
      }
      onScanned(item);
    },
    [open, onScanned],
  );

  const scan = useCallback(
    async (code: string) => {
      const known = await findByBarcode(code);
      if (known) {
        beepOk();
        take(known);
        return;
      }
      try {
        take(await api<CatalogItem>(`/catalog/barcode/${encodeURIComponent(code)}`));
        beepOk();
      } catch {
        beepUnknown();
        toast.warn(t('checkout.unknownBarcode', { code }));
      }
    },
    [take, t, toast],
  );

  useEffect(() => listenForScanner({ onScan: (c) => void scan(c) }), [scan]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (q.trim().length < 2) {
        setStyles([]);
        return;
      }
      setStyles(await searchStyles(q, 20));
    }, 180);
    return () => clearTimeout(timer);
  }, [q]);

  const nm = (x: { nameEn: string; nameAr: string }) => (lang === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr);

  // searchStyles hands the variants back in size-scale order, so the rows read
  // S, M, L and the columns follow the first appearance of each colour.
  const sizes = open ? [...new Set(open.variants.map((v) => v.size))] : [];
  const colors = open
    ? [...new Map(open.variants.map((v) => [v.colorEn, { en: v.colorEn, ar: v.colorAr, hex: v.colorHex }])).values()]
    : [];
  const cell = (size: string, colorEn: string) =>
    open?.variants.find((v) => v.size === size && v.colorEn === colorEn);
  const gridTotal = Object.values(qty).reduce((a, n) => a + (n || 0), 0);

  /** Reopening a style shows what was already entered, so it can be corrected
   *  instead of counted a second time. */
  const openStyle = (sm: StyleMatch) => {
    setOpen(sm);
    onOpenChange?.(true);
    // Only the boxes that already hold something are filled in; the rest stay
    // empty rather than showing a grid of zeros to type over.
    setQty(
      Object.fromEntries(
        sm.variants.filter((v) => (current[v.variantId] ?? 0) > 0).map((v) => [v.variantId, current[v.variantId]!]),
      ),
    );
  };

  const close = () => {
    setOpen(null);
    setQty({});
    onOpenChange?.(false);
  };

  /** True when this style is already on the delivery: clearing every box is
   *  then a real instruction (take it off), not an empty form. */
  const styleOnDelivery = open ? open.variants.some((v) => (current[v.variantId] ?? 0) > 0) : false;

  const commit = () => {
    if (!open) return;
    onSetStyle(
      open.productId,
      open.variants.map((v) => ({ item: v, qty: qty[v.variantId] || 0 })).filter((x) => x.qty > 0),
    );
    close();
    setQ('');
    setStyles([]);
  };

  if (open) {
    return (
      <div className="style-grid-wrap">
        <div className="row">
          <span className="strong">{nm(open)}</span>
          <span className="spacer" />
          <Button variant="ghost" onClick={close}>
            {t('common.back')}
          </Button>
        </div>
        <p className="xsmall muted" style={{ margin: 0 }}>{t('stock.gridLegend')}</p>
        <div className="style-grid-scroll">
          <table className="style-grid">
            <thead>
              <tr>
                <th />
                {colors.map((c) => (
                  <th key={c.en}>
                    <Swatch hex={c.hex} />
                    <span className="xsmall"> {lang === 'ar' ? c.ar : c.en}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sizes.map((size) => (
                <tr key={size}>
                  <th scope="row">{size}</th>
                  {colors.map((c) => {
                    const v = cell(size, c.en);
                    if (!v) return <td key={c.en} className="absent" />;
                    const counted = (qty[v.variantId] ?? 0) > 0;
                    return (
                      // A counted square is tinted, so halfway through a box the
                      // shopkeeper can see what is done without reading all 42.
                      <td key={c.en} className={counted ? 'filled' : undefined}>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          value={qty[v.variantId] || ''}
                          aria-label={`${size} ${lang === 'ar' ? c.ar : c.en}`}
                          /*
                           * The global input rule is five :not()s deep and
                           * outranks any class, including its 44px minimum
                           * height and 8/12 padding. Forty-two boxes at that
                           * size is a page to scroll rather than a grid to
                           * read, so the size is handed in from here and the
                           * values themselves stay in the stylesheet.
                           */
                          style={{
                            width: 'var(--grid-box, 4rem)',
                            minHeight: 'var(--grid-h, 30px)',
                            padding: 'var(--grid-pad, 3px 4px)',
                          }}
                          onFocus={(e) => e.currentTarget.select()}
                          onChange={(e) =>
                            setQty((m) => ({ ...m, [v.variantId]: Math.max(0, Number(e.target.value) || 0) }))
                          }
                        />
                        <span className="shelf">{v.stock}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pinned: on a touch till the finger-sized boxes make a long size run
            taller than the screen, and the count and the Add button must not be
            the part that falls off the bottom. */}
        <div className="row style-grid-actions">
          <span className="muted">
            {plural(gridTotal, {
              one: 'stock.piecesOne',
              two: 'stock.piecesTwo',
              few: 'stock.piecesCount',
              many: 'stock.piecesMany',
            })}
          </span>
          <span className="spacer" />
          <Button variant="primary" disabled={gridTotal === 0 && !styleOnDelivery} onClick={commit}>
            {t('stock.addToDelivery')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="row">
        <input
          type="search"
          data-scan-target="true"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('common.searchPlaceholder')} aria-label={t('common.searchPlaceholder')}
          autoFocus
        />
        <CameraButton onScan={(c) => void scan(c)} />
      </div>
      {q.trim().length >= 2 && styles.length === 0 ? (
        <p className="xsmall muted" style={{ marginBlockStart: 'var(--s-2)' }}>{t('common.noResults')}</p>
      ) : null}
      {styles.length > 0 ? (
        <div className="col" style={{ gap: 'var(--s-1)', marginBlockStart: 'var(--s-2)', maxHeight: 260, overflowY: 'auto' }}>
          {styles.map((sm) => (
            <button key={sm.productId} className="pick-row" onClick={() => openStyle(sm)}>
              <span style={{ flex: 1, textAlign: 'start' }}>
                <span className="strong">{nm(sm)}</span>
                <span className="xsmall muted">
                  {' '}· {t('stock.styleSummary', {
                    sizes: new Set(sm.variants.map((v) => v.size)).size,
                    colors: new Set(sm.variants.map((v) => v.colorEn)).size,
                  })}
                </span>
              </span>
              <span className="xsmall muted">{sm.stock}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Receiving
// ---------------------------------------------------------------------------

interface ReceiveLine {
  item: CatalogItem;
  qty: number;
  unitCost: number;
}

export function Receive() {
  const t = useT();
  const plural = usePlural();
  const { lang } = useI18n();
  const { money } = useSettings();
  const toast = useToast();
  const refs = useRefs();
  const { refreshCatalog } = useSync();

  const [lines, setLines] = useState<ReceiveLine[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [vatIncluded, setVatIncluded] = useState(true);
  const [paid, setPaid] = useState(false);
  const [details, setDetails] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printAfter, setPrintAfter] = useState<{ items: CatalogItem[]; qty: Record<string, number> } | null>(null);
  // While a size-and-colour grid is open it is the whole job; the New item
  // button and the "nothing here yet" box only compete with it.
  const [gridOpen, setGridOpen] = useState(false);

  const add = (item: CatalogItem, qty = 1, unitCost?: number) => {
    setLines((cur) => {
      const found = cur.find((l) => l.item.variantId === item.variantId);
      if (found) {
        return cur.map((l) =>
          l.item.variantId === item.variantId
            ? { ...l, qty: l.qty + qty, unitCost: unitCost ?? l.unitCost }
            : l,
        );
      }
      return [...cur, { item, qty, unitCost: unitCost ?? item.avgCost ?? 0 }];
    });
  };

  const setQty = (variantId: string, qty: number) =>
    setLines((c) =>
      qty <= 0
        ? c.filter((x) => x.item.variantId !== variantId)
        : c.map((x) => (x.item.variantId === variantId ? { ...x, qty } : x)),
    );

  const pieces = useMemo(() => lines.reduce((a, l) => a + l.qty, 0), [lines]);
  const total = useMemo(() => lines.reduce((a, l) => a + l.qty * l.unitCost, 0), [lines]);

  /*
   * A delivery of one blazer in three sizes and two colours is six variants,
   * and six separate rows read like a wall. Gather them back under the style
   * they belong to: the name and the cost are said once, the sizes sit under
   * it. This is the same grouping the search box does, kept all the way
   * through to the invoice.
   */
  const groups = useMemo(() => {
    const order: string[] = [];
    const byStyle = new Map<string, ReceiveLine[]>();
    for (const l of lines) {
      const list = byStyle.get(l.item.productId);
      if (list) list.push(l);
      else {
        byStyle.set(l.item.productId, [l]);
        order.push(l.item.productId);
      }
    }
    return order.map((productId) => {
      const rows = byStyle.get(productId)!;
      return {
        productId,
        rows,
        pieces: rows.reduce((a, l) => a + l.qty, 0),
        cost: rows.reduce((a, l) => a + l.qty * l.unitCost, 0),
        /* One cost for the style: the shopkeeper pays per piece, not per size. */
        unitCost: rows[0]!.unitCost,
        mixedCost: rows.some((l) => l.unitCost !== rows[0]!.unitCost),
      };
    });
  }, [lines]);

  /**
   * The grid says what the WHOLE of a style's delivery is, so this replaces
   * that style's lines rather than adding to them - reopening a style to fix a
   * number corrects it instead of counting it twice. The style keeps its place
   * in the list, and a cost already typed is kept.
   */
  const setStyleLines = (productId: string, picks: { item: CatalogItem; qty: number }[]) =>
    setLines((cur) => {
      const wasThere = new Map(cur.filter((l) => l.item.productId === productId).map((l) => [l.item.variantId, l]));
      const fallbackCost = [...wasThere.values()][0]?.unitCost;
      const replacement = picks.map((p) => ({
        item: p.item,
        qty: p.qty,
        unitCost: wasThere.get(p.item.variantId)?.unitCost ?? fallbackCost ?? p.item.avgCost ?? 0,
      }));

      const out: ReceiveLine[] = [];
      let placed = false;
      for (const line of cur) {
        if (line.item.productId !== productId) {
          out.push(line);
        } else if (!placed) {
          out.push(...replacement);
          placed = true;
        }
      }
      if (!placed) out.push(...replacement);
      return out;
    });

  /** What the delivery holds right now, for seeding the grid. */
  const onDelivery = useMemo(
    () => Object.fromEntries(lines.map((l) => [l.item.variantId, l.qty])),
    [lines],
  );

  /** Typing a cost on the style sets it for every size and colour under it. */
  const setStyleCost = (productId: string, unitCost: number) =>
    setLines((c) => c.map((x) => (x.item.productId === productId ? { ...x, unitCost } : x)));

  const removeStyle = (productId: string) =>
    setLines((c) => c.filter((x) => x.item.productId !== productId));

  const save = async () => {
    setBusy(true);
    try {
      await api('/stock/receive', {
        body: {
          supplierId: supplierId || null,
          invoiceNo: invoiceNo || null,
          vatIncluded,
          paid,
          lines: lines.map((l) => ({ variantId: l.item.variantId, qty: l.qty, unitCost: l.unitCost })),
        },
      });
      toast.ok(t('stock.received'));
      await refreshCatalog();
      // Offer labels for exactly what came in - the moment they are needed.
      setPrintAfter({
        items: lines.map((l) => l.item),
        qty: Object.fromEntries(lines.map((l) => [l.item.variantId, l.qty])),
      });
      setLines([]);
      setInvoiceNo('');
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const nm = (i: CatalogItem) => (lang === 'ar' ? i.nameAr || i.nameEn : i.nameEn || i.nameAr);

  return (
    <div className="col">
      {/*
        * The box, first. Scan a piece and it goes on; a style the shop has never
        * carried gets added right here rather than sending somebody off to the
        * Products screen and back with the delivery half unpacked.
        */}
      {/* Open a size run and the card is about that run; closed, it is the
          search box and spans the screen as every other card does. */}
      <div className={gridOpen ? 'card fit' : 'card'}>
        <div className="recv-add">
          <div className="grow">
            <StylePicker
              current={onDelivery}
              onScanned={(item) => add(item, 1)}
              onSetStyle={setStyleLines}
              onOpenChange={setGridOpen}
            />
          </div>
          {gridOpen ? null : (
            <Button variant="primary" size="lg" onClick={() => setCreating(true)}>
              {t('stock.newItem')}
            </Button>
          )}
        </div>

        {lines.length === 0 ? (
          gridOpen ? null : <Empty icon="📦" title={t('stock.emptyDelivery')} hint={t('stock.emptyDeliveryHint')} />
        ) : (
          <div className="recv-list">
            {groups.map((g) => (
              <div key={g.productId} className="recv-style">
                <div className="recv-style-head">
                  <div className="grow">
                    <div className="strong">{nm(g.rows[0]!.item)}</div>
                    <div className="xsmall muted">
                      {plural(g.pieces, {
                        one: 'stock.piecesOne',
                        two: 'stock.piecesTwo',
                        few: 'stock.piecesCount',
                        many: 'stock.piecesMany',
                      })}
                    </div>
                  </div>
                  <label className="recv-cost">
                    <span className="xsmall muted">{t('stock.unitCost')}</span>
                    <MoneyInput
                      value={g.unitCost}
                      onValue={(v) => setStyleCost(g.productId, v)}
                      style={{ width: 96 }}
                    />
                  </label>
                  <div className="recv-style-total tnum strong">{money(g.cost)}</div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeStyle(g.productId)}
                    aria-label={t('checkout.removeLine')}
                  >
                    ✕
                  </Button>
                </div>

                <div className="recv-variants">
                  {g.rows.map((l) => (
                    <div key={l.item.variantId} className="recv-variant">
                      <span className="grow">
                        <span className="strong">{l.item.size}</span>{' '}
                        <Swatch hex={l.item.colorHex} />{' '}
                        <span className="muted">{lang === 'ar' ? l.item.colorAr : l.item.colorEn}</span>
                      </span>
                      <div className="qty recv-row-qty">
                        <button type="button" onClick={() => setQty(l.item.variantId, l.qty - 1)} aria-label="-">
                          &minus;
                        </button>
                        <input
                          type="number"
                          min={0}
                          inputMode="numeric"
                          className="qty-num"
                          value={l.qty}
                          onFocus={(e) => e.currentTarget.select()}
                          onChange={(e) =>
                            setQty(l.item.variantId, Math.max(0, Math.floor(Number(e.target.value) || 0)))
                          }
                        />
                        <button type="button" onClick={() => setQty(l.item.variantId, l.qty + 1)} aria-label="+">
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The invoice. Needed for the books, never in the way of unpacking. */}
      {lines.length > 0 ? (
        <div className="card">
          <button type="button" className="disclose" onClick={() => setDetails((v) => !v)}>
            <span className="chev" aria-hidden>{details ? '▾' : '▸'}</span>
            {t('stock.deliveryDetails')}
            {!details && (supplierId || invoiceNo) ? (
              <span className="xsmall muted" style={{ marginInlineStart: 'var(--s-2)' }}>
                {[refs.suppliers.find((x) => x.id === supplierId)?.name, invoiceNo].filter(Boolean).join(' · ')}
              </span>
            ) : null}
          </button>

          {details ? (
            <div className="grid c3" style={{ marginBlockStart: 'var(--s-4)' }}>
              <Field label={t('common.supplier')}>
                <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                  <option value="">{t('common.none')}</option>
                  {refs.suppliers.map((x) => (
                    <option key={x.id} value={x.id}>{x.name}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('stock.invoiceNo')}>
                <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
              </Field>
              <div className="col" style={{ justifyContent: 'flex-end' }}>
                <label className="checkline">
                  <input type="checkbox" checked={vatIncluded} onChange={(e) => setVatIncluded(e.target.checked)} />
                  {t('stock.vatIncluded')}
                </label>
                <label className="checkline">
                  <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} />
                  {t('stock.markPaid')}
                </label>
              </div>
            </div>
          ) : null}

          <div className="recv-foot">
            <div>
              <div className="recv-count">
                {plural(pieces, {
                  one: 'stock.piecesOne',
                  two: 'stock.piecesTwo',
                  few: 'stock.piecesCount',
                  many: 'stock.piecesMany',
                })}
              </div>
              {total > 0 ? (
                <div className="xsmall muted">
                  {t('stock.costTotal')} <span className="tnum">{money(total)}</span>
                </div>
              ) : null}
            </div>
            <Button variant="primary" size="lg" busy={busy} onClick={() => void save()}>
              {t('stock.putIntoStock')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* A style the shop has never carried, added without leaving the delivery. */}
      {creating ? (
        <ProductCreator
          purpose="receiving"
          onClose={() => setCreating(false)}
          onCreated={(made) => {
            setCreating(false);
            if (made.items.length === 0) {
              toast.warn(t('stock.newItemNotOnList'));
              return;
            }
            for (const item of made.items) add(item, made.qty, made.unitCost);
            toast.ok(t('stock.newItemAdded', { n: made.items.length * made.qty }));
          }}
        />
      ) : null}

      {/* Straight into the label printer, with the received counts already filled in. */}
      {printAfter ? (
        <LabelPrinter items={printAfter.items} quantities={printAfter.qty} onClose={() => setPrintAfter(null)} />
      ) : null}
    </div>
  );
}

export function Adjust() {
  const t = useT();
  const { lang } = useI18n();
  const toast = useToast();
  const { refreshCatalog } = useSync();
  const [lines, setLines] = useState<{ item: CatalogItem; qty: number }[]>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const reasons = [
    { key: 'adjust_damaged', label: t('stock.damaged') },
    { key: 'adjust_lost', label: t('stock.lost') },
    { key: 'adjust_count', label: t('stock.countCorrection') },
    { key: 'adjust_sample', label: t('stock.sample') },
    { key: 'adjust_other', label: t('stock.other') },
  ];

  const save = async () => {
    setBusy(true);
    try {
      await api('/stock/adjust', {
        body: { reason, note, lines: lines.map((l) => ({ variantId: l.item.variantId, qty: l.qty })) },
      });
      toast.ok(t('stock.adjusted'));
      await refreshCatalog();
      setLines([]);
      setNote('');
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const nm = (i: CatalogItem) => (lang === 'ar' ? i.nameAr || i.nameEn : i.nameEn || i.nameAr);

  return (
    <div className="card" style={{ maxWidth: 800, marginInline: 'auto' }}>
      <h2>{t('stock.adjustTitle')}</h2>

      <Field label={t('stock.adjustReason')}>
        <div className="btn-row">
          {reasons.map((r) => (
            <Button key={r.key} variant={reason === r.key ? 'primary' : 'default'} onClick={() => setReason(r.key)}>
              {r.label}
            </Button>
          ))}
        </div>
      </Field>

      <div style={{ marginBlock: 'var(--s-4)' }}>
        <ItemPicker
          onPick={(item) =>
            setLines((cur) =>
              cur.some((l) => l.item.variantId === item.variantId) ? cur : [...cur, { item, qty: -1 }],
            )
          }
        />
      </div>

      {lines.map((l) => (
        <div key={l.item.variantId} className="ret-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="strong">{nm(l.item)}</div>
            <div className="xsmall muted">
              {l.item.size} · {lang === 'ar' ? l.item.colorAr : l.item.colorEn} · {t('common.stock')}: {l.item.stock}
            </div>
          </div>
          <input
            type="number"
            value={l.qty}
            onChange={(e) =>
              setLines((c) =>
                c.map((x) => (x.item.variantId === l.item.variantId ? { ...x, qty: Number(e.target.value) } : x)),
              )
            }
            style={{ width: 100, textAlign: 'center' }}
          />
          <Pill kind={l.qty < 0 ? 'danger' : 'ok'}>
            {l.item.stock} → {l.item.stock + l.qty}
          </Pill>
          <Button size="sm" variant="ghost" aria-label={t('checkout.removeLine')} onClick={() => setLines((c) => c.filter((x) => x.item.variantId !== l.item.variantId))}>
            ✕
          </Button>
        </div>
      ))}

      <Field label={t('common.notes')}>
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      <Button
        variant="primary"
        size="lg"
        block
        style={{ marginBlockStart: 'var(--s-4)' }}
        disabled={!reason || lines.length === 0}
        busy={busy}
        onClick={() => void save()}
      >
        {t('stock.adjust')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stock count
// ---------------------------------------------------------------------------

export function StockCount() {
  const t = useT();
  const { lang } = useI18n();
  const toast = useToast();
  const refs = useRefs();
  const { refreshCatalog } = useSync();

  const [counts, setCounts] = useState<Record<string, unknown>[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [lines, setLines] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [applying, setApplying] = useState(false);
  const [onlyDiffs, setOnlyDiffs] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      setCounts(await api<Record<string, unknown>[]>('/stock/counts'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOne = useCallback(async (id: string) => {
    try {
      const res = await api<{ count: Record<string, unknown>; lines: Record<string, unknown>[] }>(`/stock/counts/${id}`);
      setLines(res.lines);
    } catch (e) {
      toast.err((e as Error).message);
    }
  }, [toast]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (active) void loadOne(active);
  }, [active, loadOne]);

  // Scanning on the shop floor adds one to whatever was scanned.
  const scan = useCallback(
    async (code: string) => {
      if (!active) return;
      const item = await findByBarcode(code);
      if (!item) {
        beepUnknown();
        toast.warn(t('checkout.unknownBarcode', { code }));
        return;
      }
      beepOk();
      try {
        await api(`/stock/counts/${active}/scan`, { body: { variantId: item.variantId, qty: 1 } });
        await loadOne(active);
      } catch (e) {
        // A scan that did not reach the server must not look like it counted.
        beepUnknown();
        toast.err((e as Error).message);
      }
    },
    [active, loadOne, t, toast],
  );

  useEffect(() => {
    if (!active) return;
    return listenForScanner({ onScan: (c) => void scan(c) });
  }, [active, scan]);

  const create = async () => {
    try {
      const res = await api<{ id: string }>('/stock/counts', { body: { name, categoryId: categoryId || null } });
      setCreating(false);
      setActive(res.id);
      await loadList();
    } catch (e) {
      toast.err((e as Error).message);
    }
  };

  const apply = async () => {
    if (!active) return;
    try {
      const res = await api<{ adjusted: number }>(`/stock/counts/${active}/apply`, { body: {} });
      toast.ok(`${t('stock.countApplied')} (${res.adjusted})`);
      await refreshCatalog();
      await loadList();
      setActive(null);
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const diffs = lines.filter((l) => Number(l.difference) !== 0);
  const shown = onlyDiffs ? diffs : lines;

  if (loading) return <Loading />;

  if (!active) {
    return (
      <div className="col">
        <div className="row">
          <h2>{t('stock.count')}</h2>
          <div className="spacer" />
          <Button variant="primary" onClick={() => setCreating(true)}>{t('stock.countNew')}</Button>
        </div>

        {counts.length === 0 ? (
          <Empty icon="📋" title={t('stock.count')} hint={t('stock.countScan')} />
        ) : (
          <div className="card flush">
            <div className="table-wrap">
              <table className="cards">
                <thead>
                  <tr>
                    <th>{t('common.name')}</th>
                    <th>{t('common.date')}</th>
                    <th className="num">{t('common.items')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {counts.map((c) => (
                    <tr key={String(c.id)} style={{ cursor: 'pointer' }} onClick={() => setActive(String(c.id))}>
                      <td className="strong" data-label={t('common.name')}>{String(c.name)}</td>
                      <td data-label={t('common.date')}>{new Date(String(c.created_at)).toLocaleDateString('en-GB')}</td>
                      <td className="num" data-label={t('common.items')}>{Number(c.line_count)}</td>
                      <td className="num">
                        <Pill kind={String(c.status) === 'applied' ? 'ok' : 'warn'}>{String(c.status)}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {creating ? (
          <Modal
            title={t('stock.countNew')}
            onClose={() => setCreating(false)}
            footer={
              <>
                <Button onClick={() => setCreating(false)}>{t('common.cancel')}</Button>
                <div className="spacer" />
                <Button variant="primary" onClick={() => void create()}>{t('stock.countNew')}</Button>
              </>
            }
          >
            <Field label={t('stock.countName')}>
              <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Field>
            <Field label={t('stock.countScope')}>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">{t('common.all')}</option>
                {refs.categories.map((c) => (
                  <option key={c.id} value={c.id}>{lang === 'ar' ? c.name_ar : c.name_en}</option>
                ))}
              </select>
            </Field>
          </Modal>
        ) : null}
      </div>
    );
  }

  return (
    <div className="col">
      <div className="card">
        <div className="card-head">
          <h2>{t('stock.countScan')}</h2>
          <div className="spacer" />
          <Button variant="ghost" onClick={() => setActive(null)}>{t('common.back')}</Button>
        </div>
        <div className="row">
          <CameraButton onScan={(c) => void scan(c)} />
          <input
            data-scan-target="true"
            placeholder={t('common.barcode')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void scan((e.target as HTMLInputElement).value);
                (e.target as HTMLInputElement).value = '';
              }
            }}
            autoFocus
          />
          <Pill kind="info">{t('stock.countScanned', { n: lines.reduce((a, l) => a + Number(l.counted), 0) })}</Pill>
        </div>
      </div>

      <div className="card flush">
        <div className="row" style={{ padding: 'var(--s-4)' }}>
          <h3>{t('stock.countReview')}</h3>
          <div className="spacer" />
          <label className="checkline">
            <input type="checkbox" checked={onlyDiffs} onChange={(e) => setOnlyDiffs(e.target.checked)} />
            {t('stock.difference')} ({diffs.length})
          </label>
        </div>

        {diffs.length === 0 && lines.length > 0 ? (
          <p className="center muted small" style={{ paddingBlockEnd: 'var(--s-5)' }}>{t('stock.noDifferences')}</p>
        ) : null}

        <div className="table-wrap" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.product')}</th>
                <th className="num">{t('stock.expected')}</th>
                <th className="num">{t('stock.counted')}</th>
                <th className="num">{t('stock.difference')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.slice(0, 500).map((l) => {
                const d = Number(l.difference);
                return (
                  <tr key={String(l.id)}>
                    <td className="full" data-label={t('common.product')}>
                      <div className="strong">{lang === 'ar' ? String(l.name_ar) : String(l.name_en)}</div>
                      <div className="xsmall muted">
                        {String(l.size)} · {lang === 'ar' ? String(l.color_ar ?? '') : String(l.color_en ?? '')}
                      </div>
                    </td>
                    <td className="num tnum" data-label={t('stock.expected')}>{Number(l.expected)}</td>
                    <td className="num" data-label={t('stock.counted')}>
                      <input
                        type="number"
                        value={Number(l.counted)}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setLines((c) =>
                            c.map((x) => (x.id === l.id ? { ...x, counted: v, difference: v - Number(x.expected) } : x)),
                          );
                          void api(`/stock/counts/${active}/set`, {
                            body: { variantId: l.variant_id, counted: v },
                          });
                        }}
                        style={{ width: 80, textAlign: 'center' }}
                      />
                    </td>
                    <td className="num" data-label={t('stock.difference')}>
                      {d === 0 ? <span className="muted">0</span> : <Pill kind={d > 0 ? 'warn' : 'danger'}>{d > 0 ? `+${d}` : d}</Pill>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: 'var(--s-4)' }}>
          <Button variant="primary" size="lg" block onClick={() => setApplying(true)} disabled={diffs.length === 0}>
            {t('stock.countApply')} ({diffs.length})
          </Button>
        </div>
      </div>

      {applying ? (
        <Confirm
          title={t('stock.countApply')}
          message={t('stock.countApplyWarn')}
          confirmLabel={t('stock.countApply')}
          danger
          onConfirm={() => void apply()}
          onCancel={() => setApplying(false)}
        />
      ) : null}
    </div>
  );
}
